
import { supabase } from '@/integrations/supabase/client';
import { UniversalCourseRegistry } from './universalCourseRegistry';

export interface ConsistencyReport {
  id: string;
  reportType: string;
  affectedUserId?: string;
  affectedCourseId?: string;
  affectedModuleId?: string;
  inconsistencyDetails: any;
  severityLevel: 'low' | 'medium' | 'high' | 'critical';
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
}

export interface ValidationResult {
  inconsistenciesFound: number;
  details: any[];
  checkedAt: string;
  recommendations?: string[];
}

export class DataConsistencyService {
  // Validate module progress consistency
  static async validateModuleProgressConsistency(
    userId?: string,
    courseId?: string
  ): Promise<ValidationResult> {
    try {
      const { data, error } = await supabase.rpc('validate_module_progress_consistency', {
        p_user_id: userId || null,
        p_course_id: courseId || null
      });

      if (error) {
        console.error('Error validating module progress consistency:', error);
        throw error;
      }

      // Type cast the response as it comes from a database function
      const validationData = data as any;

      return {
        inconsistenciesFound: validationData.inconsistencies_found || 0,
        details: validationData.details || [],
        checkedAt: validationData.checked_at,
        recommendations: this.generateRecommendations(validationData.details || [])
      };
    } catch (error) {
      console.error('Error in consistency validation:', error);
      return {
        inconsistenciesFound: 0,
        details: [],
        checkedAt: new Date().toISOString(),
        recommendations: ['Error occurred during validation - please try again']
      };
    }
  }

  // Validate certificate eligibility data consistency
  static async validateCertificateEligibilityConsistency(
    userId: string,
    courseId: string
  ): Promise<ValidationResult> {
    try {
      // Check for certificate eligibility inconsistencies
      const { data: moduleProgress } = await supabase
        .from('user_module_progress')
        .select('module_id, progress')
        .eq('user_id', userId)
        .eq('course_id', courseId)
        .eq('progress', 100);

      const { data: quizAttempts } = await supabase
        .from('module_attempts')
        .select('module_id, score, max_score, passed')
        .eq('user_id', userId)
        .eq('course_id', courseId)
        .eq('passed', true);

      const inconsistencies = [];

      // Check each completed module has a corresponding quiz pass
      if (moduleProgress) {
        for (const progress of moduleProgress) {
          const hasQuizPass = quizAttempts?.some(
            attempt => attempt.module_id === progress.module_id && attempt.passed
          );

          if (!hasQuizPass) {
            inconsistencies.push({
              type: 'module_complete_no_quiz',
              moduleId: progress.module_id,
              issue: 'Module marked 100% complete but no passing quiz found',
              severity: 'high'
            });

            // Log the inconsistency
            await this.reportInconsistency({
              reportType: 'certificate_eligibility_error',
              affectedUserId: userId,
              affectedCourseId: courseId,
              affectedModuleId: progress.module_id,
              inconsistencyDetails: {
                moduleProgress: progress.progress,
                hasQuizPass: false,
                issue: 'Module complete without quiz pass'
              },
              severityLevel: 'high'
            });
          }
        }
      }

      return {
        inconsistenciesFound: inconsistencies.length,
        details: inconsistencies,
        checkedAt: new Date().toISOString(),
        recommendations: this.generateRecommendations(inconsistencies)
      };
    } catch (error) {
      console.error('Error validating certificate eligibility consistency:', error);
      return {
        inconsistenciesFound: 0,
        details: [],
        checkedAt: new Date().toISOString(),
        recommendations: ['Error occurred during validation']
      };
    }
  }

  // Validate course-module consistency across registry and database
  static async validateCourseModuleConsistency(courseId: string): Promise<{
    valid: boolean;
    registryModules: number;
    databaseModules: number;
    issues: string[];
  }> {
    const issues: string[] = [];
    
    // Check registry
    const registryModules = UniversalCourseRegistry.getAllModulesForCourse(courseId);
    
    // Check database
    const { data: dbModules } = await supabase
      .from('user_module_progress')
      .select('module_id')
      .eq('course_id', courseId);
      
    const uniqueDbModules = [...new Set(dbModules?.map(m => m.module_id) || [])];
    
    if (registryModules.length === 0) {
      issues.push('Course not found in UniversalCourseRegistry');
    }
    
    if (registryModules.length !== uniqueDbModules.length && uniqueDbModules.length > 0) {
      issues.push(`Module count mismatch: Registry has ${registryModules.length}, Database has ${uniqueDbModules.length}`);
    }
    
    return {
      valid: issues.length === 0,
      registryModules: registryModules.length,
      databaseModules: uniqueDbModules.length,
      issues
    };
  }

  // Validate ALL courses system-wide
  static async validateAllCoursesConsistency(): Promise<{
    totalCourses: number;
    validCourses: number;
    invalidCourses: number;
    issues: Array<{ courseId: string; courseTitle: string; issues: string[] }>;
  }> {
    const allCourses = UniversalCourseRegistry.getAllRegisteredCourses();
    const results = {
      totalCourses: allCourses.length,
      validCourses: 0,
      invalidCourses: 0,
      issues: [] as Array<{ courseId: string; courseTitle: string; issues: string[] }>
    };

    console.log(`🔍 Validating ${allCourses.length} courses...`);

    for (const course of allCourses) {
      const validation = await this.validateCourseModuleConsistency(course.courseId);
      if (validation.valid) {
        results.validCourses++;
      } else {
        results.invalidCourses++;
        results.issues.push({
          courseId: course.courseId,
          courseTitle: course.courseTitle,
          issues: validation.issues
        });
      }
    }

    console.log('✅ System-Wide Course Validation:', {
      total: results.totalCourses,
      valid: results.validCourses,
      invalid: results.invalidCourses
    });
    
    if (results.issues.length > 0) {
      console.warn('⚠️ Courses with issues:', results.issues);
    }
    
    return results;
  }

  // Report data inconsistency
  static async reportInconsistency(report: {
    reportType: string;
    affectedUserId?: string;
    affectedCourseId?: string;
    affectedModuleId?: string;
    inconsistencyDetails: any;
    severityLevel: 'low' | 'medium' | 'high' | 'critical';
  }): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('data_consistency_reports')
        .insert({
          report_type: report.reportType,
          affected_user_id: report.affectedUserId,
          affected_course_id: report.affectedCourseId,
          affected_module_id: report.affectedModuleId,
          inconsistency_details: report.inconsistencyDetails,
          severity_level: report.severityLevel
        });

      if (error) {
        console.error('Error reporting inconsistency:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in reportInconsistency:', error);
      return false;
    }
  }

  // Get consistency reports
  static async getConsistencyReports(
    filters?: {
      reportType?: string;
      severityLevel?: string;
      resolved?: boolean;
      userId?: string;
      courseId?: string;
    }
  ): Promise<ConsistencyReport[]> {
    try {
      let query = supabase
        .from('data_consistency_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.reportType) {
        query = query.eq('report_type', filters.reportType);
      }
      if (filters?.severityLevel) {
        query = query.eq('severity_level', filters.severityLevel);
      }
      if (filters?.resolved !== undefined) {
        query = query.eq('resolved', filters.resolved);
      }
      if (filters?.userId) {
        query = query.eq('affected_user_id', filters.userId);
      }
      if (filters?.courseId) {
        query = query.eq('affected_course_id', filters.courseId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching consistency reports:', error);
        return [];
      }

      return data?.map(report => ({
        id: report.id,
        reportType: report.report_type,
        affectedUserId: report.affected_user_id,
        affectedCourseId: report.affected_course_id,
        affectedModuleId: report.affected_module_id,
        inconsistencyDetails: report.inconsistency_details,
        severityLevel: report.severity_level as 'low' | 'medium' | 'high' | 'critical',
        resolved: report.resolved,
        resolvedAt: report.resolved_at,
        resolvedBy: report.resolved_by,
        createdAt: report.created_at
      })) || [];
    } catch (error) {
      console.error('Error in getConsistencyReports:', error);
      return [];
    }
  }

  // Resolve consistency report
  static async resolveConsistencyReport(
    reportId: string,
    resolvedBy: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('data_consistency_reports')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
          resolved_by: resolvedBy
        })
        .eq('id', reportId);

      if (error) {
        console.error('Error resolving consistency report:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in resolveConsistencyReport:', error);
      return false;
    }
  }

  // Run comprehensive data validation
  static async runComprehensiveValidation(
    userId?: string,
    courseId?: string
  ): Promise<{
    moduleProgressValidation: ValidationResult;
    certificateEligibilityValidation?: ValidationResult;
    totalIssues: number;
    criticalIssues: number;
  }> {
    try {
      const moduleProgressValidation = await this.validateModuleProgressConsistency(userId, courseId);
      
      let certificateEligibilityValidation;
      if (userId && courseId) {
        certificateEligibilityValidation = await this.validateCertificateEligibilityConsistency(userId, courseId);
      }

      const totalIssues = moduleProgressValidation.inconsistenciesFound + 
                         (certificateEligibilityValidation?.inconsistenciesFound || 0);

      const criticalIssues = [
        ...moduleProgressValidation.details,
        ...(certificateEligibilityValidation?.details || [])
      ].filter(issue => issue.severity === 'critical' || issue.severity === 'high').length;

      return {
        moduleProgressValidation,
        certificateEligibilityValidation,
        totalIssues,
        criticalIssues
      };
    } catch (error) {
      console.error('Error in comprehensive validation:', error);
      return {
        moduleProgressValidation: {
          inconsistenciesFound: 0,
          details: [],
          checkedAt: new Date().toISOString()
        },
        totalIssues: 0,
        criticalIssues: 0
      };
    }
  }

  // Generate recommendations based on inconsistencies
  private static generateRecommendations(inconsistencies: any[]): string[] {
    const recommendations = [];

    const moduleCompleteNoQuiz = inconsistencies.filter(i => 
      i.type === 'module_complete_no_quiz_pass' || i.type === 'module_complete_no_quiz'
    );

    if (moduleCompleteNoQuiz.length > 0) {
      recommendations.push(
        `Fix ${moduleCompleteNoQuiz.length} modules marked complete without quiz passes`
      );
      recommendations.push('Review module completion logic to ensure quiz validation');
    }

    if (inconsistencies.some(i => i.severity === 'critical')) {
      recommendations.push('Address critical inconsistencies immediately');
      recommendations.push('Consider disabling certificate generation until resolved');
    }

    if (recommendations.length === 0) {
      recommendations.push('No specific recommendations - data appears consistent');
    }

    return recommendations;
  }
}
